import FormElement from "../types/forms/FormElement";
import EnergyHarbourPermitFormState from "../types/forms/Permits/EnergyHarbourPermit/EnergyHarbourPermitFormState";
import HotWorkFormState from "../types/forms/Permits/EnergyHarbourPermit/HotWorkFormState";
import EnergyHarbourPermit from "../types/permits/EnergyHarbourPermit";
import { HotWorkConditions } from "../types/permits/EnergyHarbourPermit/HotWorkConditions";
import Errors from "../types/responses/Errors";
import { Signature_isEmpty } from "../types/Signature";
import { Array_unorderedEquals } from "./arrayUtils";
import { Date_dateOnly } from "./dateUtils";
import { HotWorkSection, PermitCategory } from "./enums";
import { String_capitalize } from "./stringUtils";

function hasErrorMessage(value: any): value is { errorMessage: string } {
    const hasErrorMessage = value && "errorMessage" in value;

    return hasErrorMessage;
}

export function FormState_PopulateErrorMessages<TFormState extends { [key: string]: any }>(
    formState: TFormState,
    errors: Errors
): TFormState {
    const formStateKeys = Object.keys(formState);

    const newFormState: TFormState = { ...formState };

    formStateKeys.forEach((k) => {
        const element = newFormState[k];

        if (!hasErrorMessage(element)) {
            return;
        }

        const capitalizedKey = String_capitalize(k);

        element.errorMessage = "";

        if (!errors[capitalizedKey] || errors[capitalizedKey].length === 0) {
            return;
        }

        const errorMessage = errors[capitalizedKey][0];
        element.errorMessage = errorMessage;
    });

    return newFormState;
}

export function FormState_clearErrors<TFormState extends { [key: string]: any }>(formState: TFormState): TFormState {
    const formStateKeys = Object.keys(formState);

    const newFormState: TFormState = { ...formState };

    formStateKeys.forEach((k) => {
        const element = newFormState[k];

        if (!hasErrorMessage(element)) {
            return;
        }

        element.errorMessage = "";
    });

    return newFormState;
}

export function hasHotWorkSignatureClearingChanges(
    storedPermit: EnergyHarbourPermit,
    formState: EnergyHarbourPermitFormState
): boolean {
    if (
        Signature_isEmpty(storedPermit.hotWorkInternalDirectorFlammableProductSignature) &&
        Signature_isEmpty(storedPermit.hotWorkExternalDirectorFlammableProductSignature)
    ) {
        return false;
    }

    const pendingDescriptionChanges =
        storedPermit.canUpdateDescription && hasDescriptionChanges(formState, storedPermit);
    const pendingPositionChanges = storedPermit.canUpdatePositions && hasPositionChanges(formState, storedPermit);
    const pendingStartDateChanges = storedPermit.canUpdateStartDate && hasStartDateChanges(formState, storedPermit);
    const pendingEndDateChanges = storedPermit.canUpdateEndDate && hasEndDateChanges(formState, storedPermit);
    const pendingPermitCategoryChanges =
        storedPermit.canManagePermitCategory && hasPermitCategoryChanges(formState, storedPermit);

    const pendingLegacyHotWorkChanges =
        !storedPermit.supportsCategorySpecificHotWorkConditions &&
        hasHotWorkChanges(formState.hotWork, storedPermit.hotWork);
    const pendingHotWorkCatOneChanges =
        storedPermit.supportsCategorySpecificHotWorkConditions &&
        hasHotWorkChanges(formState.hotWorkCategoryOneConditions, storedPermit.hotWorkCategoryOneConditions);
    const pendingHotWorkCatTwoChanges =
        storedPermit.supportsCategorySpecificHotWorkConditions &&
        hasHotWorkChanges(formState.hotWorkCategoryTwoConditions, storedPermit.hotWorkCategoryTwoConditions);
    const pendingHotWorkCatThreeChanges =
        storedPermit.supportsCategorySpecificHotWorkConditions &&
        hasHotWorkChanges(formState.hotWorkCategoryThreeConditions, storedPermit.hotWorkCategoryThreeConditions);

    return (
        pendingDescriptionChanges ||
        pendingPositionChanges ||
        pendingStartDateChanges ||
        pendingEndDateChanges ||
        pendingPermitCategoryChanges ||
        pendingLegacyHotWorkChanges ||
        pendingHotWorkCatOneChanges ||
        pendingHotWorkCatTwoChanges ||
        pendingHotWorkCatThreeChanges
    );
}

function hasDescriptionChanges(formState: EnergyHarbourPermitFormState, storedPermit: EnergyHarbourPermit): boolean {
    return formState.description.value !== storedPermit.description;
}

function hasPositionChanges(formState: EnergyHarbourPermitFormState, storedPermit: EnergyHarbourPermit): boolean {
    if (formState.positions.length !== storedPermit.positions.length) {
        return true;
    }

    const sortedFormStatePositions = [...formState.positions].sort((a, b) => a.order - b.order);
    const sortedStoredPermitPositions = [...storedPermit.positions].sort((a, b) => a.order - b.order);

    for (let i = 0; i < sortedFormStatePositions.length; i++) {
        const formStatePosition = sortedFormStatePositions[i];
        const storedPermitPosition = sortedStoredPermitPositions[i];

        const positionHasChanged =
            formStatePosition.comment !== storedPermitPosition.comment ||
            formStatePosition.longitude !== storedPermitPosition.longitude ||
            formStatePosition.latitude !== storedPermitPosition.latitude ||
            ((!!formStatePosition.polygon || !!storedPermitPosition.polygonJsonObject) &&
                JSON.stringify(formStatePosition.polygon) !== storedPermitPosition.polygonJsonObject);

        if (positionHasChanged) {
            return true;
        }
    }

    return false;
}

function hasStartDateChanges(formState: EnergyHarbourPermitFormState, storedPermit: EnergyHarbourPermit): boolean {
    return Date_dateOnly(formState.startDate.value).getTime() !== Date_dateOnly(storedPermit.startDate).getTime();
}

function hasEndDateChanges(formState: EnergyHarbourPermitFormState, storedPermit: EnergyHarbourPermit): boolean {
    return Date_dateOnly(formState.endDate.value).getTime() !== Date_dateOnly(storedPermit.endDate).getTime();
}

function hasPermitCategoryChanges(formState: EnergyHarbourPermitFormState, storedPermit: EnergyHarbourPermit): boolean {
    const hasChanges =
        !Array_unorderedEquals(formState.permitCategories.value, storedPermit.permitCategories) ||
        (storedPermit.permitCategories.includes(PermitCategory.HotWorkCategoryOne) &&
            (storedPermit.hotWorkCategoryOneOptions.angleGrinder !=
                formState.hotWorkCategoryOneOptions.angleGrinder.value ||
                storedPermit.hotWorkCategoryOneOptions.cuttingTorch !=
                formState.hotWorkCategoryOneOptions.cuttingTorch.value ||
                storedPermit.hotWorkCategoryOneOptions.gasBurner !=
                formState.hotWorkCategoryOneOptions.gasBurner.value ||
                storedPermit.hotWorkCategoryOneOptions.hotAirGun !=
                formState.hotWorkCategoryOneOptions.hotAirGun.value ||
                storedPermit.hotWorkCategoryOneOptions.openFlameSoldering !=
                formState.hotWorkCategoryOneOptions.openFlameSoldering.value ||
                storedPermit.hotWorkCategoryOneOptions.other != formState.hotWorkCategoryOneOptions.other.value ||
                storedPermit.hotWorkCategoryOneOptions.plasmaCutter !=
                formState.hotWorkCategoryOneOptions.plasmaCutter.value ||
                storedPermit.hotWorkCategoryOneOptions.welding != formState.hotWorkCategoryOneOptions.welding.value)) ||
        (storedPermit.permitCategories.includes(PermitCategory.HotWorkCategoryTwo) &&
            (storedPermit.hotWorkCategoryTwoOptions.screwDriverDrill !=
                formState.hotWorkCategoryTwoOptions.screwDriverDrill.value ||
                storedPermit.hotWorkCategoryTwoOptions.exCamera != formState.hotWorkCategoryTwoOptions.exCamera.value ||
                storedPermit.hotWorkCategoryTwoOptions.excavator !=
                formState.hotWorkCategoryTwoOptions.excavator.value ||
                storedPermit.hotWorkCategoryTwoOptions.GPS != formState.hotWorkCategoryTwoOptions.GPS.value ||
                storedPermit.hotWorkCategoryTwoOptions.megger != formState.hotWorkCategoryTwoOptions.megger.value ||
                storedPermit.hotWorkCategoryTwoOptions.multimeter !=
                formState.hotWorkCategoryTwoOptions.multimeter.value ||
                storedPermit.hotWorkCategoryTwoOptions.openEquipment !=
                formState.hotWorkCategoryTwoOptions.openEquipment.value ||
                storedPermit.hotWorkCategoryTwoOptions.other != formState.hotWorkCategoryTwoOptions.other.value ||
                storedPermit.hotWorkCategoryTwoOptions.totalStation !=
                formState.hotWorkCategoryTwoOptions.totalStation.value)) ||
        (storedPermit.permitCategories.includes(PermitCategory.HotWorkCategoryThree) &&
            (storedPermit.hotWorkCategoryThreeOptions.GPS != formState.hotWorkCategoryThreeOptions.GPS.value ||
                storedPermit.hotWorkCategoryThreeOptions.boat != formState.hotWorkCategoryThreeOptions.boat.value ||
                storedPermit.hotWorkCategoryThreeOptions.craneTruck !=
                formState.hotWorkCategoryThreeOptions.craneTruck.value ||
                storedPermit.hotWorkCategoryThreeOptions.drone != formState.hotWorkCategoryThreeOptions.drone.value ||
                storedPermit.hotWorkCategoryThreeOptions.excavator !=
                formState.hotWorkCategoryThreeOptions.excavator.value ||
                storedPermit.hotWorkCategoryThreeOptions.generator !=
                formState.hotWorkCategoryThreeOptions.generator.value ||
                storedPermit.hotWorkCategoryThreeOptions.multimeter !=
                formState.hotWorkCategoryThreeOptions.multimeter.value ||
                storedPermit.hotWorkCategoryThreeOptions.nonExCamera !=
                formState.hotWorkCategoryThreeOptions.nonExCamera.value ||
                storedPermit.hotWorkCategoryThreeOptions.openExClassifiedEquipment !=
                formState.hotWorkCategoryThreeOptions.openExClassifiedEquipment.value ||
                storedPermit.hotWorkCategoryThreeOptions.other != formState.hotWorkCategoryThreeOptions.other.value ||
                storedPermit.hotWorkCategoryThreeOptions.vacuumTruck !=
                formState.hotWorkCategoryThreeOptions.vacuumTruck.value));

    return hasChanges;
}

function hasHotWorkChanges(formStateConditions: HotWorkFormState, storedPermitHotWork: HotWorkConditions): boolean {
    const changes: Record<keyof HotWorkConditions, boolean> = {
        counterSignatureRequest: false,
        distanceFromPumpsAndVentilationMeters:
            formStateConditions.distanceFromPumpsAndVentilationMeters.value !==
            storedPermitHotWork.distanceFromPumpsAndVentilationMeters,
        fireDetectorsTurnedOffIsChecked:
            formStateConditions.fireDetectorsTurnedOffIsChecked.value !==
            storedPermitHotWork.fireDetectorsTurnedOffIsChecked,
        fireDetectorsTurnedOffSectionNames:
            formStateConditions.fireDetectorsTurnedOffSectionNames.value !==
            storedPermitHotWork.fireDetectorsTurnedOffSectionNames,
        fireExtinguisherCount:
            formStateConditions.fireExtinguisherCount.value !== storedPermitHotWork.fireExtinguisherCount,
        fireExtinguisherIsChecked:
            formStateConditions.fireExtinguisherIsChecked.value !== storedPermitHotWork.fireExtinguisherIsChecked,
        fireExtinguisherKg: formStateConditions.fireExtinguisherKg.value !== storedPermitHotWork.fireExtinguisherKg,
        fireGuardCount: formStateConditions.fireGuardCount.value !== storedPermitHotWork.fireGuardCount,
        informCloseDepotBeforeWorkIsChecked:
            formStateConditions.informCloseDepotBeforeWorkIsChecked.value !==
            storedPermitHotWork.informCloseDepotBeforeWorkIsChecked,
        isCleanedUpForFireProtection:
            formStateConditions.isCleanedUpForFireProtection.value !== storedPermitHotWork.isCleanedUpForFireProtection,
        isCloseDepotInformedBeforeWorkIsStarted:
            formStateConditions.isCloseDepotInformedBeforeWorkIsStarted.value !==
            storedPermitHotWork.isCloseDepotInformedBeforeWorkIsStarted,
        isContinousGasMeasurements:
            formStateConditions.isContinousGasMeasurements.value !== storedPermitHotWork.isContinousGasMeasurements,
        isGasDeclared: formStateConditions.isGasDeclared.value !== storedPermitHotWork.isGasDeclared,
        isPhoneClassified: formStateConditions.isPhoneClassified.value !== storedPermitHotWork.isPhoneClassified,
        isShipInformedAboutFlammablesSub30Celcius:
            formStateConditions.isShipInformedAboutFlammablesSub30Celcius.value !==
            storedPermitHotWork.isShipInformedAboutFlammablesSub30Celcius,
        isSparkProtected: formStateConditions.isSparkProtected.value !== storedPermitHotWork.isSparkProtected,
        isWaterFromFirePostInPlace:
            formStateConditions.isWaterFromFirePostInPlace.value !== storedPermitHotWork.isWaterFromFirePostInPlace,
        isWellsProtected: formStateConditions.isWellsProtected.value !== storedPermitHotWork.isWellsProtected,
        nameOfCloseDepotBeforeWorkIsStartedInfo:
            formStateConditions.nameOfCloseDepotBeforeWorkIsStartedInfo.value !==
            storedPermitHotWork.nameOfCloseDepotBeforeWorkIsStartedInfo,
        noHotWorkAtQuay: formStateConditions.noHotWorkAtQuay.value !== storedPermitHotWork.noHotWorkAtQuay,
        noHotWorkCloseToShipsHandlingFlammables:
            formStateConditions.noHotWorkCloseToShipsHandlingFlammables.value !==
            storedPermitHotWork.noHotWorkCloseToShipsHandlingFlammables,
        noHotworkWithFlammableMaterialsUsedSub30Celcius:
            formStateConditions.noHotworkWithFlammableMaterialsUsedSub30Celcius.value !==
            storedPermitHotWork.noHotworkWithFlammableMaterialsUsedSub30Celcius,
        otherInformation: formStateConditions.otherInformation.value !== storedPermitHotWork.otherInformation,
        postWorkPlaceMonitoredHours:
            formStateConditions.postWorkPlaceMonitoredHours.value !== storedPermitHotWork.postWorkPlaceMonitoredHours,
        workPlaceMonitoredIsChecked:
            formStateConditions.workPlaceMonitoredIsChecked.value !== storedPermitHotWork.workPlaceMonitoredIsChecked
    };

    const hasChanges = Object.values(changes).some((changed) => changed);

    return hasChanges;
}
